/**
 * Enterprise API Client - Core Module
 *
 * Provides centralized API request handling with:
 * - Duplicate request prevention
 * - Automatic retry logic
 * - Response caching
 * - Request queuing
 * - Unified error handling
 *
 * Part of Zanova Enterprise Architecture - Skill #2
 */

class EnterpriseAPIClient {
  constructor(config = {}) {
    // Configuration
    this.config = {
      retryAttempts: config.retryAttempts || 3,
      retryDelay: config.retryDelay || 1000, // ms
      cacheTimeout: config.cacheTimeout || 5 * 60 * 1000, // 5 minutes
      // Enable logging only in development (localhost, myshopify.com, or ?debug=true)
      enableLogging: config.enableLogging !== undefined ? config.enableLogging : (
        window.location.hostname === 'localhost' ||
        window.location.hostname.includes('127.0.0.1') ||
        window.location.hostname.includes('.myshopify.com') ||
        window.location.search.includes('debug=true')
      ),
      ...config
    };

    // Request tracking
    this.requestQueue = new Map(); // Track in-flight requests
    this.cache = new Map(); // Cache GET responses
    this.abortControllers = new Map(); // Track abort controllers for cancellation

    // Statistics (useful for debugging)
    this.stats = {
      totalRequests: 0,
      cacheHits: 0,
      cacheMisses: 0,
      errors: 0,
      retries: 0
    };

    this.log('EnterpriseAPIClient initialized', this.config);
  }

  /**
   * Main request method - handles all HTTP requests
   * @param {string} url - API endpoint URL
   * @param {object} options - Fetch options (method, headers, body, etc.)
   * @returns {Promise} - Response data
   */
  async request(url, options = {}) {
    const {
      method = 'GET',
      headers = {},
      body = null,
      cache = method === 'GET', // Only cache GET requests by default
      retry = true,
      signal = null,
      requestId = this.generateRequestId(url, options),
      ...otherOptions
    } = options;

    this.stats.totalRequests++;
    this.log(`Request #${this.stats.totalRequests}`, { url, method, requestId });

    // Check cache for GET requests
    if (cache && method === 'GET') {
      const cached = this.getFromCache(requestId);
      if (cached) {
        this.stats.cacheHits++;
        this.log(`Cache HIT for ${requestId}`, cached);
        return Promise.resolve(cached);
      }
      this.stats.cacheMisses++;
    }

    // Check if identical request is already in flight
    if (this.requestQueue.has(requestId)) {
      this.log(`Duplicate request detected, waiting for ${requestId}`);
      return this.requestQueue.get(requestId);
    }

    // Create abort controller for this request
    const abortController = new AbortController();
    this.abortControllers.set(requestId, abortController);

    // Build fetch options
    const fetchOptions = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...headers
      },
      signal: signal || abortController.signal,
      ...otherOptions
    };

    if (body && method !== 'GET') {
      fetchOptions.body = typeof body === 'string' ? body : JSON.stringify(body);
    }

    // Create the request promise
    const requestPromise = this.executeRequest(url, fetchOptions, {
      requestId,
      cache,
      retry,
      retryCount: 0
    });

    // Track in queue
    this.requestQueue.set(requestId, requestPromise);

    // Clean up after request completes
    requestPromise
      .finally(() => {
        this.requestQueue.delete(requestId);
        this.abortControllers.delete(requestId);
      });

    return requestPromise;
  }

  /**
   * Execute fetch with retry logic
   */
  async executeRequest(url, options, context) {
    const { requestId, cache, retry, retryCount } = context;

    try {
      const response = await fetch(url, options);

      // Handle HTTP errors
      if (!response.ok) {
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
        error.status = response.status;
        error.response = response;
        throw error;
      }

      // Parse response
      const contentType = response.headers.get('content-type');
      let data;

      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // Get text first
        data = await response.text();

        // Try to parse as JSON if it looks like JSON (CRITICAL FIX for Shopify /cart.js)
        if (data && (data.startsWith('{') || data.startsWith('['))) {
          try {
            data = JSON.parse(data);
          } catch (e) {
            // If JSON parsing fails, keep as text (silently)
          }
        }
      }

      // Cache successful GET responses
      if (cache && options.method === 'GET') {
        this.saveToCache(requestId, data);
      }

      this.log(`Request SUCCESS for ${requestId}`, data);
      return data;

    } catch (error) {
      this.stats.errors++;

      // Don't retry if request was aborted
      if (error.name === 'AbortError') {
        this.log(`Request ABORTED for ${requestId}`);
        throw error;
      }

      // CRITICAL: Don't retry on 422 (Unprocessable Entity) - these are business logic errors
      // 422 indicates out of stock, invalid quantity, etc. - retrying won't help
      // Only retry on network errors (500, 503, timeout, connection failed)
      const shouldNotRetry = error.status === 422 ||
                             error.status === 400 ||
                             error.status === 404 ||
                             (error.status >= 400 && error.status < 500);

      if (shouldNotRetry) {
        this.log(`Request FAILED (non-retryable ${error.status}) for ${requestId}`, error);
        throw error;
      }

      // Retry logic for transient errors (network, server errors)
      if (retry && retryCount < this.config.retryAttempts) {
        this.stats.retries++;
        const delay = this.config.retryDelay * (retryCount + 1); // Exponential backoff

        this.log(`Request RETRY ${retryCount + 1}/${this.config.retryAttempts} for ${requestId} after ${delay}ms`, error.message);

        await this.sleep(delay);

        return this.executeRequest(url, options, {
          ...context,
          retryCount: retryCount + 1
        });
      }

      // Max retries exceeded or retry disabled
      this.log(`Request FAILED for ${requestId}`, error);
      throw error;
    }
  }

  /**
   * GET request helper
   */
  async get(url, options = {}) {
    return this.request(url, { ...options, method: 'GET' });
  }

  /**
   * POST request helper
   */
  async post(url, body, options = {}) {
    return this.request(url, { ...options, method: 'POST', body });
  }

  /**
   * PUT request helper
   */
  async put(url, body, options = {}) {
    return this.request(url, { ...options, method: 'PUT', body });
  }

  /**
   * DELETE request helper
   */
  async delete(url, options = {}) {
    return this.request(url, { ...options, method: 'DELETE' });
  }

  /**
   * Abort a specific request
   */
  abortRequest(requestId) {
    const controller = this.abortControllers.get(requestId);
    if (controller) {
      controller.abort();
      this.log(`Aborted request ${requestId}`);
      return true;
    }
    return false;
  }

  /**
   * Abort all pending requests
   */
  abortAll() {
    this.abortControllers.forEach((controller, requestId) => {
      controller.abort();
      this.log(`Aborted request ${requestId}`);
    });
    this.abortControllers.clear();
    this.requestQueue.clear();
  }

  /**
   * Cache Management
   */
  getFromCache(key) {
    const cached = this.cache.get(key);
    if (!cached) return null;

    const isExpired = Date.now() - cached.timestamp > this.config.cacheTimeout;
    if (isExpired) {
      this.cache.delete(key);
      return null;
    }

    return cached.data;
  }

  saveToCache(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  clearCache() {
    this.cache.clear();
    this.log('Cache cleared');
  }

  /**
   * Generate unique request ID for deduplication
   */
  generateRequestId(url, options) {
    const method = options.method || 'GET';
    const body = options.body ? JSON.stringify(options.body) : '';
    return `${method}:${url}:${body}`;
  }

  /**
   * Utility: Sleep helper for retry delays
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Logging helper
   */
  log(message, data = null) {
    if (!this.config.enableLogging) return;

    const timestamp = new Date().toISOString();
    const prefix = '[EnterpriseAPI]';

    if (data) {
      window.debugLogger.log(`${prefix} ${timestamp} ${message}`, data);
    } else {
      window.debugLogger.log(`${prefix} ${timestamp} ${message}`);
    }
  }

  /**
   * Get queue status (useful for debugging)
   */
  getQueueStatus() {
    return {
      pendingRequests: this.requestQueue.size,
      cachedItems: this.cache.size,
      stats: { ...this.stats }
    };
  }

  /**
   * Reset statistics
   */
  resetStats() {
    this.stats = {
      totalRequests: 0,
      cacheHits: 0,
      cacheMisses: 0,
      errors: 0,
      retries: 0
    };
  }
}

// Export for use in other modules
if (typeof window !== 'undefined') {
  window.EnterpriseAPIClient = EnterpriseAPIClient;
  window.debugLogger?.log('✅ EnterpriseAPIClient loaded');
}
